import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import PageTitle from '../../../components/PageTitle';
import NavMenu from '../../navMenu/NavMenu';
import {
  getCompetitionVehicles,
  getRacingNumbers,
  addVehicle,
  deleteVehicle,
  getCompetition,
  deleteMultipleVehicles,
} from '../../../api/Competitions';
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import AddButton from '../../../components/Buttons/AddButton';
import VehiclesTable from './VehiclesTable';
import EmptyState from '../../../components/EmptyState';
import ModalCentered from '../../../components/ModalCentered';
import Button from 'react-bootstrap/esm/Button';
import CustomToast, { ToastStatesEnum } from '../../../components/CustomToast';
import VehicleCard from './VehicleCard';
import Loading from '../../../components/Loading';
import CompetitionStatus from '../CompetitionStatus';
import Spinner from 'react-bootstrap/esm/Spinner';
import { getVehiclesForBids } from '../../../api/Commerce';
import CardContainer from '../../../components/CardContainer';
import BulkActionMenu from '../../../components/BulkActionMenu';
import UploadVehicleNumbersButton from './UploadVehicleNumbersButton';
import UploadVehicleNumbersModal from './UploadVehicleNumbersModal';

function CompetitionVehicles() {
  const [competition, setCompetition] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleBidInfo, setVehicleBidInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [racingNumbers, setRacingNumbers] = useState([]);
  const [filteredRacingNumbers, setFilteredRacingNumbers] = useState([]);
  const [availableRacingNumbers, setAvailableRacingNumbers] = useState([]);
  const [selectedRacingNumbers, setSelectedRacingNumbers] = useState([]);
  const [showAddVehicleModal, setShowVehicleModal] = useState(false);
  const [showDeleteVehicleModal, setShowDeleteVehicleModal] = useState(false);
  const [showAddVehicleSuccess, setShowAddVehicleSuccess] = useState(false);
  const [showAddVehicleFail, setShowAddVehicleFail] = useState(false);
  const [vehicleIdToDelete, setVehicleIdToDelete] = useState(null);
  const [showDeleteVehicleSuccess, setShowDeleteVehicleSuccess] =
    useState(false);
  const [showDeleteVehicleFail, setShowDeleteVehicleFail] = useState(false);
  const [addVehicleErrorMessage, setAddVehicleErrorMessage] = useState(null);
  const [okToDelete, setOkToDelete] = useState(false);
  const [updating, setUpdating] = useState(false);
  const { id: competitionId } = useParams();
  const location = useLocation();

  const [batchSelectedVehicles, setBatchSelectedVehicles] = useState([]);
  const [removeMultipleModalShow, setRemoveMultipleModalShow] = useState(false);
  const [uploadModalShow, setUploadModalShow] = useState(false);

  const [batchToast, setBatchToast] = useState({
    show: false,
    type: ToastStatesEnum.success,
  });

  useEffect(() => {
    async function fetchData() {
      if (
        location.state === null ||
        Object.keys(location?.state?.competition).length === 0
      ) {
        const data = await getCompetition(competitionId);

        if (data) {
          setCompetition(data.competition);
        }
      } else {
        setCompetition(location.state.competition);
      }

      const vehicles = await getCompetitionVehicles(competitionId);
      if (vehicles) {
        setVehicles(vehicles);
        setLoading(false);
      }

      const vehicleBids = await getVehiclesForBids(competitionId);
      if (vehicleBids) {
        setVehicleBidInfo(vehicleBids);
      }

      const racingNumbers = await getRacingNumbers(competitionId);
      if (racingNumbers) {
        setRacingNumbers(racingNumbers);

        const assignedNumbers = vehicles.flatMap(
          (vehicle) => vehicle.racingNumbers
        );
        const availableRacingNumbers = racingNumbers.filter(
          (rn) => !assignedNumbers.includes(rn)
        );

        setAvailableRacingNumbers(availableRacingNumbers);
        setFilteredRacingNumbers(availableRacingNumbers);
      }
    }

    fetchData();
  }, [showAddVehicleSuccess, vehicleIdToDelete]);

  useEffect(() => {
    if (vehicleIdToDelete) {
      for (let i = 0; i < vehicleBidInfo.length; i++) {
        if (vehicleBidInfo[i].vehicleId === vehicleIdToDelete) {
          if (
            vehicleBidInfo[i].bidStatus === 'notopen' &&
            vehicleBidInfo[i].numberOfBids === 0
          ) {
            setOkToDelete(true);
          } else {
            setOkToDelete(false);
          }
        }
      }
      setShowDeleteVehicleModal(true);
    }
  }, [vehicleIdToDelete]);

  const filterRacingNumbers = (value) => {
    const filtered = availableRacingNumbers.filter((rn) =>
      rn.toString()?.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredRacingNumbers(filtered);
  };

  const handleNumberClick = (number) => {
    if (selectedRacingNumbers.includes(number)) {
      setSelectedRacingNumbers(
        selectedRacingNumbers.filter((srn) => srn !== number)
      );
    } else {
      setSelectedRacingNumbers([...selectedRacingNumbers, number]);
    }
  };

  const handleAddVehicle = async () => {
    setUpdating(true);
    setAddVehicleErrorMessage(null);

    try {
      for (const number of selectedRacingNumbers) {
        const result = await addVehicle(competitionId, [number]);

        if (result.statusCode !== 200) {
          throw new Error(
            result.message || 'Misslyckades att lägga till fordon'
          );
        }
      }

      setSelectedRacingNumbers([]);
      setShowVehicleModal(false);
      setShowAddVehicleSuccess(true);
    } catch (err) {
      setAddVehicleErrorMessage(err.message || 'Ett oväntat fel uppstod.');
      setShowAddVehicleFail(true);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteVehicle = async () => {
    if (vehicleIdToDelete && okToDelete) {
      setUpdating(true);
      const result = await deleteVehicle(competitionId, vehicleIdToDelete);
      if (result) {
        switch (result.statusCode) {
          case 200:
            setShowDeleteVehicleSuccess(true);
            break;
          default:
            setShowDeleteVehicleFail(true);
            break;
        }
        setShowDeleteVehicleModal(false);
        setVehicleIdToDelete(null);
        setUpdating(false);
      }
    } else {
      setShowDeleteVehicleModal(false);
      setVehicleIdToDelete(null);
    }
  };

  const handleUploadSuccess = async () => {
    setLoading(true);
    const vehicles = await getCompetitionVehicles(competitionId);
    setVehicles(vehicles ?? []);
    setLoading(false);
  };

  const addVehicleBtn = (
    <>
      {competition?.status === 'Pågående' ||
      competition?.status === 'Kommande' ? (
        <AddButton
          text="Lägg till fordon"
          onClick={() => setShowVehicleModal(true)}
        />
      ) : (
        <AddButton
          text="Lägg till fordon"
          onClick={() => setShowVehicleModal(true)}
          disabled={true}
        />
      )}
    </>
  );

  const confirmRemovalBtn = (
    <>
      {updating ? (
        <>
          <Spinner
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
            className="me-2"
          />
          <span className="visually-hidden">Loading...</span>
          Ta bort fordon
        </>
      ) : okToDelete ? (
        'Ta bort fordon'
      ) : (
        'OK'
      )}
    </>
  );

  const confirmAddBtn = (
    <>
      {updating ? (
        <>
          <Spinner
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
            className="me-2"
          />
          <span className="visually-hidden">Loading...</span>
          Lägg till fordon
        </>
      ) : (
        'Lägg till fordon'
      )}
    </>
  );

  const deleteVehicleModalBody = (
    <>
      {okToDelete ? (
        <div>
          Permanent radera fordonet? Du kan inte ångra den här åtgärden.
        </div>
      ) : (
        <div>Kan ej radera fordon med pågående eller avslutad budgivning!</div>
      )}
    </>
  );

  let vehiclesSection;

  if (loading) {
    vehiclesSection = (
      <div className="d-flex justify-content-center align-items-center mt-5">
        <Loading size="100px" text="Laddar fordon" />
      </div>
    );
  } else if (!loading && vehicles?.length === 0) {
    vehiclesSection = (
      <EmptyState
        headingText="Inga fordon skapade"
        bodyText="Lägg till fordon och koppla de till tävlingsnummer efter besiktningen."
        action={addVehicleBtn}
      />
    );
  } else {
    vehiclesSection = (
      <>
        <Col className="d-none d-md-flex flex-column mt-2">
          {
            <VehiclesTable
              vehicles={vehicles}
              onDeleteVehicle={(id) => {
                setVehicleIdToDelete(id);
              }}
              batchSelectedVehicles={batchSelectedVehicles}
              setBatchSelectedVehicles={setBatchSelectedVehicles}
              rowsPerPage={20}
            />
          }
        </Col>
        <Col className="d-flex flex-column gap-3 d-md-none mt-4">
          <CardContainer
            batchSelectedElements={batchSelectedVehicles}
            setBatchSelectedElements={setBatchSelectedVehicles}
            data={vehicles}
            cardDataPropName="vehicle"
            cardDataKey="vehicleId"
            useCheckboox={true}
            cards={vehicles.map((v, index) => {
              return (
                <VehicleCard
                  key={index}
                  vehicle={v}
                  onDeleteVehicle={(id) => {
                    setVehicleIdToDelete(id);
                  }}
                  batchSelectedVehicles={batchSelectedVehicles}
                  setBatchSelectedVehicles={setBatchSelectedVehicles}
                />
              );
            })}
            rowsPerPage={20}
          />
        </Col>
      </>
    );
  }

  return (
    <div>
      <Row>
        <Col>
          <PageTitle
            title={competition?.basicInformation?.name}
            includeLinkBack={true}
            linkBackUrl="/organizer/competitions"
            includeStatus={
              <CompetitionStatus
                status={competition?.status}
                id={competitionId}
              />
            }
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NavMenu competition={competition} />
        </Col>
      </Row>
      <Row>
        <Col className="d-flex flex-column flex-md-row justify-content-between">
          <div style={{ flexBasis: '50%' }}>
            <h4>Fordon</h4>
            <p className="text-secondary">
              Skapa fordon när besiktning är genomförd och godkänd.
              Tävlingsnummer kommer automatiskt att kopplas till fordonet när
              startlistan är skapad.
            </p>
          </div>
          <div>
            <UploadVehicleNumbersButton
              text="Ladda upp fordonsnummer"
              className="w-100"
              onClick={() => setUploadModalShow(true)}
            />
          </div>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col>{addVehicleBtn}</Col>
      </Row>
      <Row>{vehiclesSection}</Row>

      <ModalCentered
        disabled={selectedRacingNumbers.length === 0 ? true : false}
        header="Nytt fordon"
        show={showAddVehicleModal}
        onHide={() => {
          setShowVehicleModal(false);
          setSelectedRacingNumbers([]);
          setAddVehicleErrorMessage(null);
        }}
        confirmBtnText={confirmAddBtn}
        body={
          <div className="d-flex flex-column gap-3">
            <div className="text-secondary">
              Lägg till ett nytt fordon till tävlingen när det är besiktat och
              godkänt.
            </div>
            <div>
              <Form.Control
                onChange={(e) => filterRacingNumbers(e.target.value)}
                placeholder="Sök tävlingsnummer"
              />
            </div>
            <div className="d-flex flex-wrap gap-2">
              {filteredRacingNumbers.map((rn) => {
                return (
                  <Button
                    disabled={selectedRacingNumbers.includes(rn)}
                    onClick={() => handleNumberClick(rn)}
                    key={rn}
                    variant={
                      selectedRacingNumbers.includes(rn)
                        ? 'primary'
                        : 'outline-primary'
                    }
                  >
                    {rn}
                  </Button>
                );
              })}
            </div>
            <div>
              {selectedRacingNumbers?.length > 0 && (
                <span className="d-block mb-3">Valda nummer</span>
              )}
              <div className="d-flex gap-2">
                {selectedRacingNumbers.map((srn) => (
                  <span
                    onClick={() => handleNumberClick(srn)}
                    key={srn}
                    role="button"
                    className="bg-secondary-black text-white p-1 d-flex gap-2 align-items-center hover-opacity"
                    title="Klicka för att ta bort numret"
                  >
                    {srn} <span>|</span>
                    <span
                      style={{ fontSize: '20px' }}
                      className="material-symbols-outlined"
                    >
                      close
                    </span>
                  </span>
                ))}
              </div>
              {addVehicleErrorMessage && (
                <div className="text-danger mt-3">{addVehicleErrorMessage}</div>
              )}
            </div>
          </div>
        }
        onConfirm={() => handleAddVehicle()}
      />

      <ModalCentered
        header="Ta bort fordon"
        show={showDeleteVehicleModal}
        onHide={() => setShowDeleteVehicleModal(false)}
        confirmBtnText={confirmRemovalBtn}
        onConfirm={() => {
          handleDeleteVehicle();
        }}
        body={deleteVehicleModalBody}
      />

      <ModalCentered
        header="Ta bort flera fordon"
        body={<span>Är du säker på att du vill ta bort valda fordon?</span>}
        confirmBtnText={'Ta bort'}
        onConfirm={async () => {
          const result = await deleteMultipleVehicles(
            competitionId,
            batchSelectedVehicles.map((v) => v.vehicleId)
          );

          if (result.statusCode === 200) {
            setVehicles(result.vehicles);
            setBatchToast({ show: true, type: ToastStatesEnum.success });
          } else {
            setBatchToast({ show: true, type: ToastStatesEnum.error });
          }

          setRemoveMultipleModalShow(false);
        }}
        show={removeMultipleModalShow}
        onHide={() => setRemoveMultipleModalShow(false)}
      />

      <UploadVehicleNumbersModal
        competitionId={competitionId}
        show={uploadModalShow}
        onHide={() => setUploadModalShow(false)}
        onSuccess={handleUploadSuccess}
      />

      <CustomToast
        type="success"
        title="Fordon tillagt"
        body="Fordonet har lagts till"
        show={showAddVehicleSuccess}
        onClose={() => setShowAddVehicleSuccess(false)}
      />
      <CustomToast
        type="danger"
        title="Fordon ej tillagt"
        body="Fordonet kunde inte läggas till"
        show={showAddVehicleFail}
        onClose={() => setShowAddVehicleFail(false)}
      />
      <CustomToast
        type="success"
        title="Fordon borttaget"
        body="Fordonet har tagits bort"
        show={showDeleteVehicleSuccess}
        onClose={() => setShowDeleteVehicleSuccess(false)}
      />
      <CustomToast
        type="danger"
        title="Fordon ej borttaget"
        body="Fordonet kunde inte tas bort"
        show={showDeleteVehicleFail}
        onClose={() => setShowDeleteVehicleFail(false)}
      />
      <CustomToast
        type="success"
        title="Fordon borttagna"
        body="Fordonen har tagits bort"
        show={batchToast.show && batchToast.type === ToastStatesEnum.success}
        onClose={() => setBatchToast({ ...batchToast, show: false })}
      />
      <CustomToast
        type="danger"
        title="Fordon ej borttagna"
        body="Fordonen kunde inte tas bort"
        show={batchToast.show && batchToast.type === ToastStatesEnum.error}
        onClose={() => setBatchToast({ ...batchToast, show: false })}
      />

      <BulkActionMenu
        show={batchSelectedVehicles.length > 0}
        selectedElements={batchSelectedVehicles}
        onSelectAll={() => {
          setBatchSelectedVehicles(vehicles);
        }}
        onClose={() => {
          setBatchSelectedVehicles([]);
        }}
        allElementsCount={vehicles?.length}
        onDelete={() => {
          setRemoveMultipleModalShow(true);
        }}
      ></BulkActionMenu>
    </div>
  );
}

export default CompetitionVehicles;
